import type { ColumnDef } from "@tanstack/react-table";
import { api } from "@glasshalf/api-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ConfirmImpactDialog } from "../../components/ConfirmImpactDialog";
import { DataTable } from "../../components/DataTable";
import { EmptyState } from "../../components/EmptyState";
import { SavedViews, type SavedView } from "../../components/SavedViews";
import { StatusBadge } from "../../components/StatusBadge";
import { formatDateTime, formatNumber } from "../../lib/format";
import { auditLogs, locations } from "../../lib/selectors";
import type { AnyRecord, AppData, DrawerState } from "../../lib/types";

type SettingsPageProps = {
  data: AppData;
  drawer: DrawerState;
  setDrawer: (drawer: DrawerState) => void;
};

export function SettingsPage({ data, drawer, setDrawer }: SettingsPageProps) {
  const [params, setParams] = useSearchParams();
  const activeView = params.get("view") || "account";
  const roles = data.settingsRoles as AnyRecord;
  const accountRows = accountUsers(data);
  const views: SavedView[] = [
    { key: "account", label: "계정", count: accountRows.length || 1 },
    { key: "locations", label: "재고 위치", count: locations(data).length },
    { key: "audit", label: "감사 로그", count: auditLogs(data).length }
  ];
  useEffect(() => {
    if (drawer?.type !== "settings") return;
    if (activeView === "account" && !isAccountRecord(drawer.record)) setDrawer(null);
    if (activeView === "locations" && !isLocationRecord(drawer.record)) setDrawer(null);
    if (activeView === "channels" && isChannelRecord(drawer.record)) setDrawer(null);
    if (activeView === "audit" && (isAccountRecord(drawer.record) || isLocationRecord(drawer.record) || isChannelRecord(drawer.record))) setDrawer(null);
  }, [activeView, drawer, setDrawer]);
  return (
    <div className="page-stack">
      <section className="toolbar-panel">
        <SavedViews views={views} active={activeView} onChange={(view) => setParams(view === "account" ? {} : { view })} />
      </section>
      {activeView === "locations" ? <LocationsSettings data={data} drawer={drawer} setDrawer={setDrawer} /> : null}
      {activeView === "channels" ? <IntegrationsMovedPanel /> : null}
      {activeView === "audit" ? <AuditSettings data={data} drawer={drawer} setDrawer={setDrawer} /> : null}
      {activeView === "account" ? <AccountSettings data={data} drawer={drawer} setDrawer={setDrawer} /> : null}
    </div>
  );
}

function IntegrationsMovedPanel() {
  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2>판매채널 연결</h2>
          <p>Cafe24와 외부 서비스 연결 관리는 Integrations로 이동했습니다.</p>
        </div>
        <Link className="text-link" to="/integrations">Integrations 열기</Link>
      </div>
    </section>
  );
}

function AccountSettings({ data, drawer, setDrawer }: SettingsPageProps) {
  const roles = data.settingsRoles as AnyRecord;
  const roleRows = Array.isArray(roles.roles) ? roles.roles : [];
  const rows = accountUsers(data);
  const selectedId = drawer?.type === "settings" && isAccountRecord(drawer.record) ? String(drawer.record.id || drawer.record.settingsKind || "") : "";
  const columns = useMemo<ColumnDef<AnyRecord>[]>(() => [
    {
      accessorKey: "displayName",
      header: "사용자",
      cell: ({ row }) => (
        <div className="cell-primary">
          <strong>{row.original.displayName || row.original.loginId || "-"}</strong>
          <span>{row.original.loginId || "-"}</span>
        </div>
      ),
      size: 260
    },
    { accessorKey: "email", header: "이메일", cell: ({ row }) => row.original.email || "-" },
    { id: "roles", header: "역할", cell: ({ row }) => accountRoleLabel(row.original) },
    { accessorKey: "status", header: "상태", cell: ({ row }) => accountStatusBadge(row.original.status) },
    { accessorKey: "updatedAt", header: "최근 변경", cell: ({ row }) => row.original.updatedAt ? formatDateTime(row.original.updatedAt) : "-" }
  ], []);
  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2>계정</h2>
          <p>백오피스에 로그인할 운영 계정과 역할을 관리합니다.</p>
        </div>
        <button
          type="button"
          className="primary"
          onClick={() => setDrawer({ type: "settings", record: newAccountRecord(roleRows) })}
        >
          계정 추가
        </button>
      </div>
      <DataTable
        columns={columns}
        data={rows}
        getRowId={(row) => String(row.id || row.loginId || "")}
        selectedId={selectedId}
        onSelect={(record) => setDrawer({ type: "settings", record: { ...record, settingsKind: "account" } })}
        emptyTitle="등록된 계정이 없습니다."
      />
    </section>
  );
}

function LocationsSettings({ data, drawer, setDrawer }: SettingsPageProps) {
  const rows = locations(data);
  const selectedId = drawer?.type === "settings" ? String(drawer.record.code || "") : "";
  const columns = useMemo<ColumnDef<AnyRecord>[]>(() => [
    {
      accessorKey: "name",
      header: "위치",
      cell: ({ row }) => (
        <div className="cell-primary">
          <strong>{row.original.name || row.original.code}</strong>
          <span>{row.original.code}</span>
        </div>
      ),
      size: 260
    },
    { accessorKey: "locationType", header: "유형", cell: ({ row }) => locationTypeLabel(row.original) },
    {
      accessorKey: "isSellable",
      header: "재고 상태",
      cell: ({ row }) => isSellable(row.original) ? <StatusBadge tone="success">판매 가능</StatusBadge> : <StatusBadge tone="warning">비판매</StatusBadge>
    },
    { id: "allocation", header: "온라인몰 할당", cell: ({ row }) => allocationLabel(row.original) }
  ], []);
  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2>재고 위치</h2>
          <p>실제 재고를 보관하거나 운영상 분리해야 하는 오프라인 위치만 관리합니다.</p>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={rows}
        getRowId={(row) => String(row.code || row.name || "")}
        selectedId={selectedId}
        onSelect={(record) => setDrawer({ type: "settings", record })}
        emptyTitle="등록된 재고 위치가 없습니다."
      />
    </section>
  );
}

export function ChannelSettings({ data, drawer, setDrawer }: SettingsPageProps) {
  const providers = channelRows(data);
  const selectedId = drawer?.type === "settings" ? String(drawer.record.provider || drawer.record.label || "") : "";
  const columns = useMemo<ColumnDef<AnyRecord>[]>(() => [
    {
      accessorKey: "label",
      header: "채널",
      cell: ({ row }) => (
        <div className="cell-primary">
          <strong>{row.original.label || row.original.provider}</strong>
          <span>{row.original.provider || "-"}</span>
        </div>
      ),
      size: 260
    },
    {
      accessorKey: "status",
      header: "연결 상태",
      cell: ({ row }) => channelStatusBadge(row.original)
    },
    { id: "configured", header: "설정", cell: ({ row }) => channelConfiguredLabel(row.original) },
    { accessorKey: "mallId", header: "계정", cell: ({ row }) => row.original.mallId || "-" },
    { accessorKey: "lastConnectedAt", header: "최근 연결", cell: ({ row }) => row.original.lastConnectedAt ? formatDateTime(row.original.lastConnectedAt) : "-" }
  ], []);
  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2>판매채널 연결</h2>
          <p>온라인몰 주문 읽기, 상품/SKU 매핑, 재고 적용에 필요한 연결 상태를 한곳에서 확인합니다.</p>
        </div>
      </div>
      {providers.length ? (
        <DataTable
          columns={columns}
          data={providers}
          getRowId={(row) => String(row.provider || row.label || "")}
          selectedId={selectedId}
          onSelect={(record) => setDrawer({ type: "settings", record })}
          emptyTitle="연결된 판매채널이 없습니다."
        />
      ) : <EmptyState title="판매채널 연결 정보가 없습니다." body="Cafe24 연결 설정을 저장하면 이곳에 표시됩니다." />}
    </section>
  );
}

function AuditSettings({ data, drawer, setDrawer }: SettingsPageProps) {
  const rows = auditLogs(data);
  const selectedId = drawer?.type === "settings" ? String(drawer.record.id || "") : "";
  const columns = useMemo<ColumnDef<AnyRecord>[]>(() => [
    { accessorKey: "createdAt", header: "시간", cell: ({ row }) => formatDateTime(row.original.createdAt) },
    { accessorKey: "actorLabel", header: "사용자", cell: ({ row }) => row.original.actorLabel || row.original.actor || "Owner" },
    {
      accessorKey: "action",
      header: "작업",
      cell: ({ row }) => (
        <div className="cell-primary">
          <strong>{row.original.action || "-"}</strong>
          <span>{row.original.resourceType || "-"} / {row.original.resourceId || "-"}</span>
        </div>
      ),
      size: 320
    }
  ], []);
  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2>감사 로그</h2>
          <p>위험 작업과 외부 영향 작업의 변경 이력을 확인합니다.</p>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={rows}
        getRowId={(row) => String(row.id || `${row.action}:${row.createdAt}`)}
        selectedId={selectedId}
        onSelect={(record) => setDrawer({ type: "settings", record })}
        emptyTitle="감사 로그가 없습니다."
      />
    </section>
  );
}

export function SettingsDrawerContent({ record, setDrawer }: { record: AnyRecord; setDrawer: (drawer: DrawerState) => void }) {
  if (isAccountRecord(record)) return <AccountSettingsDrawer record={record} setDrawer={setDrawer} />;
  if (isLocationRecord(record)) return <LocationSettingsDrawer record={record} setDrawer={setDrawer} />;
  if (isChannelRecord(record)) return <ChannelSettingsDrawer record={record} setDrawer={setDrawer} />;
  return (
    <Section title="상세">
      <KeyValue label="이름" value={record.label || record.name || record.action || "-"} />
      <KeyValue label="상태" value={record.status || record.implementationStatus || record.resourceType || "-"} />
      <KeyValue label="식별자" value={record.provider || record.resourceId || record.id || "-"} />
    </Section>
  );
}

function ChannelSettingsDrawer({ record, setDrawer }: { record: AnyRecord; setDrawer: (drawer: DrawerState) => void }) {
  if (record.provider !== "cafe24") return <PlannedChannelDrawer record={record} />;
  return <Cafe24SettingsDrawer record={record} setDrawer={setDrawer} />;
}

function Cafe24SettingsDrawer({ record, setDrawer }: { record: AnyRecord; setDrawer: (drawer: DrawerState) => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(() => cafe24Form(record));
  const [error, setError] = useState("");
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnectReason, setDisconnectReason] = useState("");
  const status = channelStatus(record);
  const dirty = cafe24FormKey(form) !== cafe24FormKey(cafe24Form(record));
  const saveMutation = useMutation({
    mutationFn: () => api.saveOnlineMall({
      id: record.id,
      provider: "cafe24",
      name: "Cafe24",
      status: status === "connected" ? "connected" : "configured",
      authType: "oauth2",
      mallId: form.mallId,
      clientId: form.clientId,
      clientSecret: form.clientSecret,
      scopes: form.scopes,
      apiVersion: form.apiVersion,
      redirectUri: form.redirectUri
    }),
    onSuccess: async (payload) => {
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
      setError("");
      setForm((prev) => ({ ...prev, clientSecret: "" }));
      setDrawer({ type: "settings", record: { ...record, ...form, clientSecret: "", hasClientSecret: Boolean(form.clientSecret || record.hasClientSecret), status: status === "connected" ? "connected" : "configured" } });
      if (payload.message) setError("");
    },
    onError: (mutationError) => setError((mutationError as Error).message)
  });
  const oauthMutation = useMutation({
    mutationFn: api.startCafe24OAuth,
    onSuccess: (payload) => {
      const authUrl = String((payload as AnyRecord).authUrl || "");
      if (authUrl) window.location.assign(authUrl);
    },
    onError: (mutationError) => setError((mutationError as Error).message)
  });
  const disconnectMutation = useMutation({
    mutationFn: () => api.disconnectCafe24({ reason: disconnectReason }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
      setDisconnectOpen(false);
      setDisconnectReason("");
      setDrawer({ type: "settings", record: { ...record, status: "configured", connected: false, lastConnectedAt: "" } });
    },
    onError: (mutationError) => setError((mutationError as Error).message)
  });

  useEffect(() => {
    setForm(cafe24Form(record));
    setError("");
    setDisconnectOpen(false);
    setDisconnectReason("");
  }, [record]);

  const canStartOAuth = Boolean(form.mallId.trim() && form.clientId.trim() && (form.clientSecret.trim() || record.hasClientSecret));

  return (
    <>
      <Section title="연결 상태">
        <div className="drawer-status-line">{channelStatusBadge(record)}</div>
        <KeyValue label="Mall ID" value={record.mallId || "-"} />
        <KeyValue label="Client ID" value={record.clientId ? maskValue(record.clientId) : "-"} />
        <KeyValue label="Client Secret" value={record.hasClientSecret ? "저장됨" : "미저장"} />
        <KeyValue label="최근 연결" value={record.lastConnectedAt ? formatDateTime(record.lastConnectedAt) : "-"} />
      </Section>

      <Section title="Cafe24 API 설정">
        <div className="drawer-form">
          <label>
            Mall ID
            <input value={form.mallId} onChange={(event) => setForm((prev) => ({ ...prev, mallId: event.target.value }))} placeholder="예: glasshalf" />
          </label>
          <label>
            Client ID
            <input value={form.clientId} onChange={(event) => setForm((prev) => ({ ...prev, clientId: event.target.value }))} />
          </label>
          <label>
            Client Secret
            <input
              type="password"
              value={form.clientSecret}
              onChange={(event) => setForm((prev) => ({ ...prev, clientSecret: event.target.value }))}
              placeholder={record.hasClientSecret ? "저장된 secret 유지" : "Cafe24 앱 Client Secret"}
            />
          </label>
          <label>
            Redirect URI
            <input value={form.redirectUri} onChange={(event) => setForm((prev) => ({ ...prev, redirectUri: event.target.value }))} />
          </label>
          <label>
            권한 범위
            <input value={form.scopes} onChange={(event) => setForm((prev) => ({ ...prev, scopes: event.target.value }))} />
          </label>
          <label>
            API Version
            <input value={form.apiVersion} onChange={(event) => setForm((prev) => ({ ...prev, apiVersion: event.target.value }))} />
          </label>
        </div>
        <p className="muted">Redirect URI는 Cafe24 Developers 앱 설정에도 동일하게 등록되어 있어야 합니다.</p>
        {error ? <p className="form-error">{error}</p> : null}
        <div className="drawer-action-row">
          <button type="button" className="primary" disabled={!dirty || !form.mallId.trim() || !form.clientId.trim() || saveMutation.isPending} onClick={() => saveMutation.mutate()}>
            {saveMutation.isPending ? "저장 중" : "설정 저장"}
          </button>
          <button type="button" disabled={!canStartOAuth || oauthMutation.isPending} onClick={() => oauthMutation.mutate()}>
            {oauthMutation.isPending ? "이동 중" : "Cafe24 인증 시작"}
          </button>
        </div>
      </Section>

      <Section title="연결 관리">
        <p className="muted">연결 해제는 저장된 Cafe24 토큰만 제거합니다. 상품, SKU 매핑, 백오피스 할당 기준은 삭제하지 않습니다.</p>
        <button type="button" className="danger" disabled={status !== "connected" || disconnectMutation.isPending} onClick={() => setDisconnectOpen(true)}>
          {disconnectMutation.isPending ? "해제 중" : "Cafe24 연결 해제"}
        </button>
      </Section>

      <ConfirmImpactDialog
        open={disconnectOpen}
        title="Cafe24 연결 해제 확인"
        currentValue={channelStatusText(record)}
        nextValue="설정 저장됨 · 인증 토큰 없음"
        externalImpact="Cafe24 주문 읽기, 상품 가져오기, 재고 대조 같은 API 작업이 다시 인증하기 전까지 중지됩니다."
        auditTrail="cafe24.oauth.disconnect · cafe24"
        reason={disconnectReason}
        confirmLabel="연결 해제"
        tone="danger"
        busy={disconnectMutation.isPending}
        onReasonChange={setDisconnectReason}
        onCancel={() => setDisconnectOpen(false)}
        onConfirm={() => disconnectMutation.mutate()}
      />
    </>
  );
}

function PlannedChannelDrawer({ record }: { record: AnyRecord }) {
  return (
    <>
      <Section title="연결 상태">
        <div className="drawer-status-line"><StatusBadge tone="warning">준비 중</StatusBadge></div>
        <KeyValue label="채널" value={record.label || record.provider || "-"} />
        <KeyValue label="현재 작업" value="Cafe24 이후 순차 연동" />
      </Section>
      <Section title="설정">
        <p className="muted">아직 이 채널의 API 저장/인증 흐름은 열지 않았습니다. 실제 연동 범위가 확정되면 이 드로어에서 계정 정보, 인증 방식, 재고 적용 정책을 설정하게 됩니다.</p>
      </Section>
    </>
  );
}

function LocationSettingsDrawer({ record, setDrawer }: { record: AnyRecord; setDrawer: (drawer: DrawerState) => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(() => locationForm(record));
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");
  const allocation = locationAllocationSummary(record);
  const nextRecord = { ...record, ...form, isSellable: form.sellableState === "sellable" ? 1 : 0, channelType: form.locationType };
  const dirty = locationFormKey(form) !== locationFormKey(locationForm(record));
  const mutation = useMutation({
    mutationFn: () => api.saveLocation({ ...nextRecord, reason }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
      await queryClient.invalidateQueries({ queryKey: ["inventory"] });
      setConfirmOpen(false);
      setReason("");
      setDrawer({ type: "settings", record: nextRecord });
    },
    onError: (mutationError) => setError((mutationError as Error).message)
  });

  useEffect(() => {
    setForm(locationForm(record));
    setReason("");
    setConfirmOpen(false);
    setError("");
  }, [record]);

  return (
    <>
      <Section title="위치 상태">
        <KeyValue label="위치 코드" value={record.code || "-"} />
        <KeyValue label="재고 상태" value={isSellable(record) ? "판매 가능" : "비판매"} />
      </Section>

      <Section title="온라인몰 할당">
        <div className="drawer-status-line">
          <StatusBadge tone={allocation.active ? "success" : "default"}>{allocation.statusLabel}</StatusBadge>
        </div>
        {allocation.active ? (
          <>
            <KeyValue label="채널" value={allocation.channels} />
            <KeyValue label="할당 SKU" value={`${formatNumber(allocation.skuCount)} SKU`} />
            <KeyValue label="할당 기준 수량" value={`${formatNumber(allocation.quantity)}개`} />
            <p className="muted">SKU별 온라인몰 할당 기준은 Inventory의 온라인몰 할당 화면에서 조정합니다.</p>
          </>
        ) : (
          <p className="muted">현재 이 오프라인 위치를 기준으로 저장된 온라인몰 할당 수량이 없습니다.</p>
        )}
      </Section>

      <Section title="위치 편집">
        <div className="drawer-form">
          <label>
            위치명
            <input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} />
          </label>
          <label>
            유형
            <select value={form.locationType} onChange={(event) => setForm((prev) => ({ ...prev, locationType: event.target.value }))}>
              {LOCATION_TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
          <label>
            재고 상태
            <select value={form.sellableState} onChange={(event) => setForm((prev) => ({ ...prev, sellableState: event.target.value }))}>
              <option value="sellable">판매 가능</option>
              <option value="non_sellable">비판매</option>
            </select>
          </label>
          <label>
            정렬 순서
            <input
              type="number"
              min={0}
              value={form.sortOrder}
              onChange={(event) => setForm((prev) => ({ ...prev, sortOrder: Math.max(0, Number(event.target.value || 0)) }))}
            />
          </label>
        </div>
        {error ? <p className="form-error">{error}</p> : null}
        <button type="button" className="primary" disabled={!dirty || !form.name.trim()} onClick={() => setConfirmOpen(true)}>위치 저장</button>
      </Section>

      <ConfirmImpactDialog
        open={confirmOpen}
        title="재고 위치 변경 확인"
        currentValue={locationSummary(record)}
        nextValue={locationSummary(nextRecord)}
        externalImpact="온라인몰 실제 재고는 변경하지 않습니다. 위치의 판매 가능 여부는 입고, 이동, 온라인몰 할당 기준 선택에 영향을 줍니다."
        auditTrail={`settings.location.save · ${record.code}`}
        reason={reason}
        confirmLabel="위치 저장"
        busy={mutation.isPending}
        onReasonChange={setReason}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => mutation.mutate()}
      />
    </>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return <section className="drawer-section"><h3>{title}</h3>{children}</section>;
}

function KeyValue({ label, value }: { label: string; value: ReactNode }) {
  return <div className="key-value"><dt>{label}</dt><dd>{value}</dd></div>;
}

function accountUsers(data: AppData): AnyRecord[] {
  const settings = data.settingsRoles as AnyRecord;
  const roles = Array.isArray(settings.roles) ? settings.roles as AnyRecord[] : [];
  const users = Array.isArray(settings.users) ? settings.users as AnyRecord[] : [];
  const currentUser = data.status?.user as AnyRecord | null | undefined;
  const fallbackUser = currentUser ? [{
    id: currentUser.id || 1,
    displayName: currentUser.displayName || currentUser.loginId || "Owner",
    loginId: currentUser.loginId || currentUser.displayName || "owner",
    email: currentUser.email || "",
    status: currentUser.status || "active",
    roles: Array.isArray(currentUser.roles) ? currentUser.roles.map((name: string) => ({ name })) : [],
    updatedAt: ""
  }] : [];
  return (users.length ? users : fallbackUser).map((user) => ({ ...user, settingsKind: "account", availableRoles: roles }));
}

function newAccountRecord(roles: AnyRecord[]): AnyRecord {
  return {
    settingsKind: "account-new",
    displayName: "새 계정",
    loginId: "",
    email: "",
    status: "active",
    roles: [],
    availableRoles: roles
  };
}

function isAccountRecord(record: AnyRecord): boolean {
  return record.settingsKind === "account" || record.settingsKind === "account-new";
}

function accountAvailableRoles(record: AnyRecord): AnyRecord[] {
  return Array.isArray(record.availableRoles) ? record.availableRoles : [];
}

function accountRoleIds(record: AnyRecord): number[] {
  return Array.isArray(record.roles)
    ? record.roles.map((role) => Number((role as AnyRecord).id)).filter(Boolean)
    : [];
}

function accountForm(record: AnyRecord) {
  return {
    displayName: String(record.displayName || ""),
    loginId: String(record.loginId || ""),
    email: String(record.email || ""),
    status: String(record.status || "active") === "inactive" ? "inactive" : "active",
    password: "",
    roleIds: accountRoleIds(record)
  };
}

function accountFormKey(form: ReturnType<typeof accountForm>): string {
  return JSON.stringify({ ...form, password: form.password ? "__changed__" : "" });
}

function accountRoleLabel(record: AnyRecord): ReactNode {
  const names = Array.isArray(record.roles)
    ? record.roles.map((role) => String((role as AnyRecord).name || role)).filter(Boolean)
    : [];
  return names.length ? names.join(", ") : "-";
}

function accountStatusBadge(status: unknown) {
  return String(status || "active") === "inactive" ? <StatusBadge tone="warning">사용 중지</StatusBadge> : <StatusBadge tone="success">사용 중</StatusBadge>;
}

function AccountSettingsDrawer({ record, setDrawer }: { record: AnyRecord; setDrawer: (drawer: DrawerState) => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(() => accountForm(record));
  const [error, setError] = useState("");
  const isNew = record.settingsKind === "account-new";
  const dirty = accountFormKey(form) !== accountFormKey(accountForm(record));
  const mutation = useMutation({
    mutationFn: () => api.saveAccount({
      id: isNew ? undefined : record.id,
      displayName: form.displayName,
      loginId: form.loginId,
      email: form.email,
      status: form.status,
      password: form.password,
      roleIds: form.roleIds
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
      await queryClient.invalidateQueries({ queryKey: ["status"] });
      setError("");
      setDrawer(null);
    },
    onError: (mutationError) => setError((mutationError as Error).message)
  });

  useEffect(() => {
    setForm(accountForm(record));
    setError("");
  }, [record]);

  const roles = accountAvailableRoles(record);
  const canSave = Boolean(form.displayName.trim() && form.loginId.trim() && form.roleIds.length && (isNew ? form.password.length >= 10 : dirty));

  return (
    <>
      <Section title={isNew ? "새 계정" : "계정 상태"}>
        {isNew ? (
          <p className="muted">새 계정은 저장 후 바로 로그인할 수 있습니다. 초기 비밀번호는 별도로 안전하게 전달하세요.</p>
        ) : (
          <>
            <KeyValue label="로그인 ID" value={record.loginId || "-"} />
            <KeyValue label="상태" value={accountStatusBadge(record.status)} />
            <KeyValue label="역할" value={accountRoleLabel(record)} />
          </>
        )}
      </Section>

      <Section title="계정 편집">
        <div className="drawer-form">
          <label>
            표시 이름
            <input value={form.displayName} onChange={(event) => setForm((prev) => ({ ...prev, displayName: event.target.value }))} />
          </label>
          <label>
            로그인 ID
            <input value={form.loginId} onChange={(event) => setForm((prev) => ({ ...prev, loginId: event.target.value }))} autoComplete="username" />
          </label>
          <label>
            이메일
            <input value={form.email} onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))} type="email" />
          </label>
          <label>
            상태
            <select value={form.status} onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))}>
              <option value="active">사용 중</option>
              <option value="inactive">사용 중지</option>
            </select>
          </label>
          <label>
            {isNew ? "임시 비밀번호" : "비밀번호 재설정"}
            <input
              value={form.password}
              onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
              type="password"
              autoComplete={isNew ? "new-password" : "off"}
              placeholder={isNew ? "10자 이상" : "변경할 때만 입력"}
            />
          </label>
        </div>
        <fieldset className="drawer-checkbox-list">
          <legend>역할</legend>
          {roles.map((role) => (
            <label key={role.id}>
              <input
                type="checkbox"
                checked={form.roleIds.includes(Number(role.id))}
                onChange={(event) => {
                  const roleId = Number(role.id);
                  setForm((prev) => ({
                    ...prev,
                    roleIds: event.target.checked ? [...new Set([...prev.roleIds, roleId])] : prev.roleIds.filter((id) => id !== roleId)
                  }));
                }}
              />
              <span>{role.name}</span>
            </label>
          ))}
        </fieldset>
        {error ? <p className="form-error">{error}</p> : null}
        <button type="button" className="primary" disabled={!canSave || mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? "저장 중" : isNew ? "계정 추가" : "계정 저장"}
        </button>
      </Section>
    </>
  );
}

function isLocationRecord(record: AnyRecord): boolean {
  return Boolean(record.code && ("locationType" in record || "sellableState" in record || "isSellable" in record));
}

function locationForm(location: AnyRecord) {
  return {
    code: String(location.code || ""),
    name: String(location.name || ""),
    locationType: String(location.locationType || "warehouse"),
    sellableState: String(location.sellableState || (isSellable(location) ? "sellable" : "non_sellable")),
    sortOrder: Number(location.sortOrder || 100)
  };
}

function locationFormKey(form: ReturnType<typeof locationForm>): string {
  return JSON.stringify(form);
}

function locationSummary(location: AnyRecord): string {
  return [
    String(location.name || location.code || "-"),
    locationTypeLabel(location),
    isSellable(location) ? "판매 가능" : "비판매",
    `정렬 ${formatNumber(Number(location.sortOrder || 0))}`
  ].join(" · ");
}

export function isSellable(location: AnyRecord): boolean {
  return Number(location.isSellable ?? 0) === 1 || location.sellableState === "sellable";
}

const LOCATION_TYPE_OPTIONS = [
  { value: "warehouse", label: "창고" },
  { value: "store", label: "매장" },
  { value: "internal", label: "내부" },
  { value: "receiving", label: "검수" },
  { value: "hold", label: "보류" },
  { value: "damaged", label: "불량" }
];

export function locationTypeLabel(location: AnyRecord): string {
  return Object.fromEntries(LOCATION_TYPE_OPTIONS.map((option) => [option.value, option.label]))[
    String(location.locationType || "")
  ] || String(location.locationType || "-");
}

function locationAllocationSummary(location: AnyRecord) {
  const skuCount = Number(location.channelAllocationSkuCount || 0);
  const quantity = Number(location.channelAllocationQuantity || 0);
  const channelNames = Array.isArray(location.channelAllocationChannelNames) ? location.channelAllocationChannelNames.map(String).filter(Boolean) : [];
  const active = Boolean(location.hasChannelAllocations) || skuCount > 0 || quantity > 0;
  const channels = channelNames.length ? channelNames.join(", ") : active ? "온라인몰" : "-";
  return {
    active,
    channels,
    skuCount,
    quantity,
    statusLabel: active ? `${channels} 사용 중 · ${formatNumber(skuCount)} SKU · ${formatNumber(quantity)}개` : "미사용"
  };
}

function allocationLabel(location: AnyRecord) {
  const allocation = locationAllocationSummary(location);
  return allocation.active ? <StatusBadge tone="success">{allocation.statusLabel}</StatusBadge> : <StatusBadge>미사용</StatusBadge>;
}

function channelRows(data: AppData): AnyRecord[] {
  const providers = Array.isArray(data.onlineMallApis?.providers) ? data.onlineMallApis.providers as AnyRecord[] : [];
  const connections = Array.isArray(data.onlineMallApis?.connections) ? data.onlineMallApis.connections as AnyRecord[] : [];
  const connectionByProvider = new Map(connections.map((connection) => [String(connection.provider || ""), connection]));
  const recommendedRedirectUri = String(data.onlineMallApis?.recommendedRedirectUri || "");
  return providers.map((provider) => {
    const connection = connectionByProvider.get(String(provider.provider || "")) || {};
    const exactStatus = String(connection.status || "");
    return {
      ...provider,
      ...connection,
      label: connection.label || provider.label || provider.name || provider.provider,
      provider: provider.provider || connection.provider,
      status: exactStatus || provider.status,
      authType: connection.authType || provider.authType,
      nativeOAuth: Boolean(provider.nativeOAuth || connection.nativeOAuth),
      implementationStatus: provider.implementationStatus || connection.implementationStatus,
      channelCode: provider.channelCode || connection.channelCode,
      recommendedRedirectUri,
      connected: exactStatus === "connected"
    };
  });
}

function isChannelRecord(record: AnyRecord): boolean {
  return Boolean(record.provider && ("authType" in record || "implementationStatus" in record || "nativeOAuth" in record));
}

function channelStatus(record: AnyRecord): "connected" | "configured" | "planned" {
  if (record.status === "connected" || record.connected === true) return "connected";
  if (record.provider === "cafe24" && (record.mallId || record.clientId || record.hasClientSecret || record.status === "configured")) return "configured";
  return "planned";
}

function channelStatusText(record: AnyRecord): string {
  const status = channelStatus(record);
  if (status === "connected") return "연결됨";
  if (status === "configured") return "설정 저장됨";
  return "준비 중";
}

function channelStatusBadge(record: AnyRecord) {
  const status = channelStatus(record);
  if (status === "connected") return <StatusBadge tone="success">연결됨</StatusBadge>;
  if (status === "configured") return <StatusBadge tone="info">설정 저장됨</StatusBadge>;
  return <StatusBadge tone="warning">준비 중</StatusBadge>;
}

function channelConfiguredLabel(record: AnyRecord): ReactNode {
  if (record.provider !== "cafe24") return <StatusBadge>미지원</StatusBadge>;
  if (record.mallId && record.clientId && record.hasClientSecret) return <StatusBadge tone="success">설정 완료</StatusBadge>;
  if (record.mallId || record.clientId || record.hasClientSecret) return <StatusBadge tone="warning">추가 필요</StatusBadge>;
  return <StatusBadge tone="warning">설정 필요</StatusBadge>;
}

function cafe24Form(record: AnyRecord) {
  return {
    mallId: String(record.mallId || ""),
    clientId: String(record.clientId || ""),
    clientSecret: "",
    redirectUri: String(record.redirectUri || record.recommendedRedirectUri || "https://backoffice.glasshalf.co/api/cafe24/oauth/callback"),
    scopes: String(record.scopes || "mall.read_product,mall.write_product,mall.read_order,mall.write_order"),
    apiVersion: String(record.apiVersion || "2025-06-01")
  };
}

function cafe24FormKey(form: ReturnType<typeof cafe24Form>): string {
  return JSON.stringify(form);
}

function maskValue(value: string): string {
  if (!value) return "-";
  if (value.length <= 8) return `${value.slice(0, 2)}••••`;
  return `${value.slice(0, 4)}••••${value.slice(-4)}`;
}
